import * as React from 'react';
import { Theme, alpha } from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import Divider from '@mui/material/Divider';
import * as yup from 'yup';
import { useFormik, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useUpdateSpotBringableMutation, Spot, UpdateSpotBringableInput } from '@graphql';
import { useToastError } from '@hooks';

interface RenderSwitchProps {
  bringable: keyof UpdateSpotBringableInput;
}
interface SpotBringableProps {
  isActive: boolean;
  account: Spot;
}

export function SpotBringable(props: Readonly<SpotBringableProps>) {
  const { isActive, account } = props;
  const toastError = useToastError();
  const { t } = useTranslation('cta');

  const [updateSpotBringable] = useUpdateSpotBringableMutation();

  const initialValues = React.useMemo(() => {
    return Object.entries(account.bringable)
      .filter(([key, value]) => !['__typename', 'id', 'updatedAt'].includes(key))
      .reduce(
        (acc, [key, value]) => {
          acc[key as keyof UpdateSpotBringableInput] = value;
          return acc;
        },
        {} as Record<keyof UpdateSpotBringableInput, boolean | null>
      );
  }, [account.bringable]);

  const validationSchema = React.useMemo(() => {
    return yup.object(
      Object.keys(account.bringable)
        .filter((key) => !['__typename', 'id', 'updatedAt'].includes(key))
        .reduce(
          (acc, key) => {
            acc[key as keyof UpdateSpotBringableInput] = yup.boolean().nullable().defined();
            return acc;
          },
          {} as Record<keyof UpdateSpotBringableInput, yup.BooleanSchema<boolean | null>>
        )
    );
  }, [account.bringable]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      if (formik.dirty) {
        updateSpotBringable({
          variables: { input: values },
          onCompleted: ({ updateSpotBringable }) => {
            formik.resetForm({ values });
            toast(t('spotBringable.toast.success', { ns: 'field' }), { type: 'success' });
          },
          onError: toastError,
        });
      }
    },
  });

  React.useEffect(() => {
    return () => {
      // trigger the mutation when the component is not anymore in view and the form is dirty
      if (isActive && formik.dirty) {
        formik.submitForm();
      }
    };
  }, [isActive, formik.dirty]);

  if (account?.__typename !== 'Spot') {
    return null;
  }

  return (
    <Stack width="100%" spacing={3}>
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{
          p: 3,
          borderRadius: (theme: Theme) => theme.borderRadius,
          backgroundColor: alpha('#fff', 0.1),
        }}
      >
        <Stack>
          <Typography fontWeight="bold">{t('spotBringable.label', { ns: 'field' })}</Typography>
          <Typography variant="caption" color="text.secondary">
            {t('spotBringable.caption', { ns: 'field' })}
          </Typography>
        </Stack>
      </Stack>
      <Stack
        spacing={2}
        divider={<Divider sx={{ backgroundColor: (theme: Theme) => theme.palette.grey[800] }} />}
      >
        {Object.keys(account.bringable)
          .filter((key) => !['__typename', 'id', 'updatedAt'].includes(key))
          .map((bringable) => (
            <Stack
              key={bringable}
              spacing={2}
              direction="row"
              width="100%"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="caption" color="text.primary">
                {t(`bringable.${bringable}`, { ns: 'spot' })}
              </Typography>
              <RenderSwitch bringable={bringable as RenderSwitchProps['bringable']} formik={formik} />
            </Stack>
          ))}
      </Stack>
    </Stack>
  );
}

function RenderSwitch(props: RenderSwitchProps & { formik: FormikProps<any> }) {
  const { bringable, formik } = props;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    formik.setFieldValue(name, checked);
  };

  return (
    <Switch name={bringable} checked={!!formik.values[bringable]} onChange={handleChange} color={'success'} />
  );
}
